import { FlightSeatPreferenceMap } from "@/types/flights/flight-booking"
import { Nullable, Optional } from "@/types/util"
import { z } from "zod"

import { SubscribeList } from "@/components/settings-util"
import { beforeDayObject, browserTimeZone, today } from "./date-utils"

const CARD_PATTERNS = {
  VI: /^4[0-9]{12}(?:[0-9]{3})?$/,
  // 51-55 and the 2221-2720 range
  MC: /^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$/,
  AX: /^3[47][0-9]{13}$/,
  // 6011, 644-649, and 65
  DS: /^6(?:011|4[4-9][0-9]|5[0-9]{2})[0-9]{12}$/,
}

export function detectCreditCardType(number: string) {
  for (const [cardType, pattern] of Object.entries(CARD_PATTERNS)) {
    const stripped = number.replace(/\D/g, "")
    if (stripped.match(pattern)) {
      return cardType
    }
  }
  return ""
}

// Luhn checksum, which every card number satisfies.
function hasValidCardChecksum(digits: string) {
  let sum = 0
  for (let i = 0; i < digits.length; i++) {
    let digit = +digits[digits.length - 1 - i]
    if (i % 2 === 1) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    sum += digit
  }
  return sum % 10 === 0
}

const NAMEREGEX = /^[\p{L}\p{M}'-]+(\s[\p{L}\p{M}'-]+)*$/u

export const FirstNameSchema = z.object({
  // Test the schema
  // parseName({ firstName: "John" }); // Valid
  // parseName({ firstName: "O'Reilly" }); // Valid, apostrophe allowed
  // parseName({ firstName: "Anne-Marie" }); // Valid, hyphen allowed
  // parseName({ firstName: "จอห์น" }); // Valid for Thai characters
  // parseName({ firstName: "Smith O'Neil" }); // Valid, apostrophe and space allowed
  // parseName({ firstName: "John123" }); // Invalid due to numbers
  // parseName({ firstName: "Anne-Marie O'Neill" }); // Valid with hyphen, apostrophe, and space
  first_name: z
    .string()
    .min(1, "First name is required")
    .regex(NAMEREGEX, "First name cannot contain special characters"),
})

// Profile names are display names, not travel documents: no character rules, as on iOS.
export const FirstNameOptionalSchema = z.object({
  first_name: z.string().optional(),
})

export const MiddleNameSchema = z.object({
  middle_name: z
    .string()
    .regex(
      /^([\p{L}\p{M}'-]+(\s[\p{L}\p{M}'-]+)*)?$/u,
      "Middle name cannot contain special characters"
    )
    .optional(),
})

export const LastNameSchema = z.object({
  last_name: z
    .string()
    .min(1, "Last name is required")
    .regex(NAMEREGEX, "Last name cannot contain special characters"),
})

export const LastNameOptionalSchema = z.object({
  last_name: z.string().optional(),
})

export const UserNameSchema = z.object({
  ...FirstNameSchema.shape,
  ...MiddleNameSchema.shape,
  ...LastNameSchema.shape,
})

export const GenderSchema = z.object({
  gender: z.enum(["M", "F"], {
    error: (iss) =>
      iss.input === undefined ? "Gender is required" : undefined,
  }),
})

export const EmailSchema = z.object({
  email: z.email("Invalid email address"),
})

export const AddressSchema = z.object({
  street1: z.string().min(1, "Street address is required"),
  street2: z.string(),
  city: z.string().min(1, "City is required"),
  state: z.string().optional(),
  zip_code: z
    .string()
    .min(1, "Zip code is required")
    .regex(
      /^[A-Za-z0-9 \-,\\]+$/,
      "Zip code can only contain letters (A-Z, a-z), numbers (0-9), hyphens (-), and commas (,)"
    ),
})

export const AddressCountrySchema = z
  .object({
    country_code: z.string().length(2),
  })
  .extend(AddressSchema.shape)

export const SubscribeSchema = z.object({
  subscribe_state: z.enum(
    SubscribeList.map((item) => item.value) as [string, ...string[]]
  ),
})

const customSeatPreferenceErrorMap: z.core.$ZodErrorMap = (issue) => {
  if (issue.code === "invalid_value" && issue.input === "") {
    return `Value must be: ${Object.values(FlightSeatPreferenceMap).join(", ")}`
  }
  return undefined
}

export const SeatPreferenceSchema = z.object({
  seat_preference: z
    .enum(
      Object.keys(FlightSeatPreferenceMap).concat("__none__") as [
        string,
        ...string[],
      ],
      {
        error: customSeatPreferenceErrorMap,
      }
    )
    .optional()
    .nullable()
    .transform((val) => (val === "__none__" ? null : val)),
})

export const KnownTravelerNumberSchema = z.object({
  known_traveler_number: z
    .string()
    .transform((s) => s.trim().replace(/[^a-zA-Z0-9]/g, "")) // Remove non-alphanumeric characters
    .refine(
      (s) => s.length === 0 || /^[a-zA-Z0-9]{9}$/.test(s),
      "Known traveler number must be exactly 9 characters"
    ),
})

export const RedressNumberSchema = z.object({
  redress_number: z
    .string()
    .refine(
      (s) => s.trim().length === 0 || s.replace(/\D/g, "").length === 7,
      "Redress number must be 7 digits"
    )
    .transform((s) => s.trim().replace(/\D/g, "")),
})

export const BirthDaySchema = z.object({
  date_of_birth: z
    .object(
      {
        day: z.number().int(),
        month: z.number().int(),
        year: z.number().int(),
      },
      { error: "Enter your date of birth" }
    )
    .refine((x) => {
      if (x) {
        const current = today(browserTimeZone())
        return beforeDayObject(x, current)
      }
      return true
    }, "Date of birth must be in the past"),
})

export const PassportExpirationDaySchema = z.object({
  passport_expiration: z
    .object({
      day: z.number().int().min(1).max(31),
      month: z.number().int().min(1).max(12),
      year: z.number().int(),
    })
    .optional()
    .refine((x) => {
      if (x) {
        const current = today(browserTimeZone())
        return !beforeDayObject(x, current)
      }
      return true
    }, "Passport expiration date must be in the future"),
})

export const LapInfantSchema = z.object({
  lap_infant: z
    .enum(["yes", "no"])
    .optional()
    .transform((x) => {
      if (x === "yes") {
        return true
      }
      return false
    }),
})

export const MealPreferenceSchema = z.object({
  meal_preference: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === "__none__" ? null : val)),
})

export const FrequentFlyerSchema = z.object({
  frequent_flyer_numbers: z.array(
    z.object({
      airline_iata: z.string(),
      value: z.string(),
    })
  ),
})

export const FlightSeatSchema = z.object({
  flight_seats: z.array(
    z.object({ flight_leg_id: z.number(), seat: z.unknown() })
  ),
})

export interface BookingFormOptions {
  locations: {
    country_code: string
    name: string
    regions: Optional<{ code: string; name: string }[]>
  }[]
  credit_card_types: { code: string; name: string }[]
}

export type CreditCardState = [
  CreditCardFormValue,
  Nullable<CreditCardFormValidated>,
  Nullable<CreditCardFormErrors>,
]

export type ValidatedCreditCardState = [
  CreditCardFormValue,
  CreditCardFormValidated,
  null,
]

export interface CreditCardFormValue {
  card_number: string
  card_type: string | undefined
  name_on_card: string
  expiration: string
  cvv_code: string
}

export function emptyCreditCardFormValue(): CreditCardFormValue {
  return {
    card_number: "",
    card_type: undefined,
    name_on_card: "",
    expiration: "",
    cvv_code: "",
  }
}

/**
 * Formats card number input as digit groups: 4-6-5 for American Express
 * (15 digits), 4-4-4-4 otherwise (16 digits). Extra digits are dropped.
 */
export function formatCardNumber(raw: string): string {
  const digits = raw.replace(/\D/g, "")
  const isAmex = /^3[47]/.test(digits)
  const groups = isAmex ? [4, 6, 5] : [4, 4, 4, 4]
  const parts: string[] = []
  let start = 0
  for (const size of groups) {
    const part = digits.slice(start, start + size)
    if (!part) break
    parts.push(part)
    start += size
  }
  return parts.join(" ")
}

const EXPIRATION_PATTERN = /^(0[1-9]|1[0-2]) \/ (\d{2})$/

/**
 * Formats typed, pasted, or autofilled card expiration input as `MM / YY`.
 * `previous` is the current field value, used to detect deletion so the user
 * can backspace through the ` / ` separator.
 */
export function formatCardExpiration(raw: string, previous: string): string {
  const trimmed = raw.trim()
  const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})$/)
  if (isoMatch) {
    return `${isoMatch[2].padStart(2, "0")} / ${isoMatch[1].slice(2)}`
  }
  const slashMatch = trimmed.match(/^(\d{1,2})\s*\/\s*(\d{2}|\d{4})$/)
  if (slashMatch) {
    return `${slashMatch[1].padStart(2, "0")} / ${slashMatch[2].slice(-2)}`
  }

  let digits = raw.replace(/\D/g, "")
  if (digits.length === 6) {
    digits = digits.slice(0, 2) + digits.slice(4)
  }
  if (/^[2-9]/.test(digits) || (digits.length === 1 && raw.includes("/"))) {
    digits = `0${digits}`
  }
  digits = digits.slice(0, 4)

  if (digits.length < 2) return digits
  if (digits.length === 2) {
    const deletedSeparator =
      raw.length < previous.length && previous.replace(/\D/g, "").length === 2
    return deletedSeparator ? digits : `${digits} / `
  }
  return `${digits.slice(0, 2)} / ${digits.slice(2)}`
}

const creditCardFormSchema = z.object({
  card_number: z
    .string()
    .refine((s) => {
      const digits = s.replace(/\D/g, "")
      return (
        digits.length >= 13 &&
        digits.length <= 16 &&
        hasValidCardChecksum(digits)
      )
    }, "Credit card number is invalid")
    .transform((s) => s.trim().replace(/\D/g, "")),
  card_type: z.string().min(1),
  name_on_card: z.string().min(1, "Name on card is required"),
  expiration: z
    .string()
    .regex(EXPIRATION_PATTERN, "Expiration date must be MM / YY")
    .refine((s) => {
      const match = s.match(EXPIRATION_PATTERN)
      if (!match) return true

      const month = +match[1]
      const year = 2000 + +match[2]
      const current = today(browserTimeZone())
      return (
        year > current.year || (year === current.year && month >= current.month)
      )
    }, "Expiration date must be in the future"),
  cvv_code: z
    .string()
    .min(3, "CVV code must be 3 or 4 digits")
    .max(4, "CVV code must be 3 or 4 digits")
    .regex(/^\d*$/, "CVV code must be numeric"),
})

export type CreditCardFormErrors = z.core.$ZodFormattedError<
  z.output<typeof creditCardFormSchema>
>

export type CreditCardFormValidated = Omit<
  z.infer<typeof creditCardFormSchema>,
  "expiration"
> & {
  expiration_month: string
  expiration_year: string
}

function unacceptedCardTypeMessage(acceptedNames: string[]) {
  if (acceptedNames.length === 0) return "This card type isn't accepted"
  const list = new Intl.ListFormat("en", { type: "disjunction" }).format(
    acceptedNames
  )
  return `This card type isn't accepted. Use ${list}.`
}

export function validateCreditCardFormValue(
  value: CreditCardFormValue,
  formOptions: BookingFormOptions
): [true, CreditCardFormValidated, null] | [false, null, CreditCardFormErrors] {
  const acceptedCardTypes = formOptions.credit_card_types
  const schemaWithOptions = creditCardFormSchema.extend({
    // A number that passes the card_number checks but matches no supported
    // brand has an empty card_type, so it gets this message too.
    card_type: z
      .string()
      .default("")
      .refine(
        (cardType) =>
          acceptedCardTypes.some((option) => option.code === cardType),
        unacceptedCardTypeMessage(acceptedCardTypes.map(({ name }) => name))
      ),
  })

  const res = schemaWithOptions.safeParse(value)

  if (res.success) {
    const { expiration, ...rest } = res.data
    const [month, year] = expiration.split(" / ")
    return [
      true,
      { ...rest, expiration_month: month, expiration_year: `20${year}` },
      null,
    ]
  }

  return [false, null, z.formatError(res.error)]
}

export async function validateHandle(
  handle: string | undefined,
  userApi: {
    checkHandle: (
      handle: string
    ) => Promise<{ available: boolean; reserved?: boolean; suggested?: string }>
  },
  setHandleError: (error: string | undefined) => void
): Promise<boolean> {
  async function isValidHandle(handle: string): Promise<boolean> {
    try {
      const handleInfo = await userApi.checkHandle(handle)
      if (!handleInfo.available) {
        if (handleInfo.reserved) {
          setHandleError("Contact us to claim your business")
        } else {
          setHandleError("Username is taken")
        }
        return false
      }
      return true
    } catch (e) {
      return false
    }
  }

  if (!handle || handle.length < 3) {
    setHandleError("Username must be at least 3 characters")
    return false
  }

  if (handle.length > 30) {
    setHandleError("Username must be at most 30 characters")
    return false
  }

  if (!/^[a-z0-9.\-_]+$/.test(handle.toLowerCase())) {
    setHandleError(
      "Username can only contain letters, numbers, dots, hyphens, and underscores"
    )
    return false
  }

  if (handle !== handle.toLowerCase()) {
    setHandleError("Username must only be in lower case")
    return false
  }

  const valid = await isValidHandle(handle)
  if (valid) {
    setHandleError(undefined)
  }
  return valid
}
